import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import usePaymentStatusChecker from '../hooks/usePaymentStatusChecker';

/**
 * 3DS authentication modal used by the new Payment Linking flow.
 *
 * Two completion signals drive the modal close:
 *
 *   1. **postMessage from the iframe.** Nicepay's 3DS result page
 *      posts `{type:'3ds_complete', success}` to the parent — when we
 *      receive it we call onSuccess / onFailure immediately.
 *
 *   2. **BE status polling.** Midtrans's hosted 3DS finish page does
 *      NOT postMessage; left to its own devices the iframe stalls on
 *      Midtrans's "Transaction is pending. Tap to Continue." fallback
 *      page indefinitely, even after the BE webhook has settled the
 *      transaction. The polling loop here calls
 *      `checkPaymentLinkingStatus(linkCode, transactionId)` every 3
 *      seconds while the modal is open and closes on terminal status.
 *      The Nicepay path benefits from this too as a defensive fallback
 *      if the postMessage is ever lost (sandbox, popup blocker, etc.).
 *
 * Either signal racing the other is fine — both call onSuccess /
 * onFailure which (in the parent) close the modal exactly once.
 *
 * Props:
 *   - linkCode (string, required for polling): payment-link code from
 *     the URL.
 *   - transactionId (string, required for polling): the transaction
 *     created by the synchronous /process call. When either is
 *     missing the modal still works, but the postMessage fallback
 *     becomes the only completion path (ie. Midtrans will stall).
 */
const ThreeDSModal = ({
  isOpen,
  redirectUrl,
  linkCode,
  transactionId,
  onClose,
  onSuccess,
  onFailure,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [iframeSrc, setIframeSrc] = useState(null);
  const blobUrlRef = useRef(null);

  // Build iframe src: HTML content → blob URL, plain URL → use directly
  useEffect(() => {
    if (!isOpen || !redirectUrl) {
      setIsLoading(true);
      setIframeSrc(null);
      return;
    }

    // NicePay 3DS returns an HTML form string, not a URL
    if (redirectUrl.trim().startsWith('<')) {
      const blob = new Blob([redirectUrl], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setIframeSrc(url);
    } else {
      setIframeSrc(redirectUrl);
    }

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [isOpen, redirectUrl]);

  // Listen for postMessage from iframe (Nicepay completion signal)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === '3ds_complete') {
        if (event.data.success) {
          onSuccess();
        } else {
          onFailure(event.data.resultMessage || 'Payment verification failed');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSuccess, onFailure]);

  // Status polling fallback. See file-level comment for rationale.
  // Wrap the callback so React's identity-sensitive deps in the hook
  // don't trip every render — the body only depends on the stable
  // onSuccess / onFailure functions from the parent.
  const handleStatusChange = useCallback(
    (status, result) => {
      if (status === 'success') {
        onSuccess();
      } else if (status === 'failed') {
        onFailure(result?.error_message || result?.message || 'Payment verification failed');
      }
      // 'no_transaction' / 'error' are non-terminal here — keep
      // polling until Midtrans's webhook drives the row to
      // success/failed or the user closes the modal manually.
    },
    [onSuccess, onFailure],
  );

  usePaymentStatusChecker(
    linkCode,
    transactionId,
    isOpen && Boolean(linkCode) && Boolean(transactionId),
    3000,
    handleStatusChange,
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold" style={{ color: "#3B3B3B" }}>Secure Payment Verification</h2>
            <p className="text-xs sm:text-sm mt-1" style={{ color: "#515164" }}>Please complete the verification process</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" style={{ color: "#515164" }} />
          </button>
        </div>

        {/* Iframe Container */}
        <div className="flex-1 relative overflow-hidden" style={{ minHeight: '300px' }}>
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-10" style={{ backgroundColor: "#F9FAFC" }}>
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: "#01793B", borderRightColor: "transparent" }}></div>
                <p className="text-xs sm:text-sm font-medium" style={{ color: "#515164" }}>Loading secure verification...</p>
              </div>
            </div>
          )}

          {iframeSrc && (
            <iframe
              src={iframeSrc}
              className="w-full h-full border-0"
              style={{ minHeight: '300px' }}
              title="3DS Authentication"
              onLoad={() => setIsLoading(false)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 text-center" style={{ backgroundColor: "#F9FAFC" }}>
          <p className="text-[10px] sm:text-xs" style={{ color: "#828282" }}>
            Secured by your card provider. Please do not close this window.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThreeDSModal;
